/**
 * Tax Playground V2 API service (S3-P4 — direct-node chat & turn debugger).
 *
 * Promise-based wrapper around the 6 endpoints under `api/v1/TaxPlaygroundV2`
 * (TaxPlaygroundV2Controller). The axios/base-URL/cookie-auth/unwrap skeleton is
 * copied VERBATIM from `services/taxConfigV2Api.js` (house style of the V2
 * family) — only `ROOT` and the method list differ, so the three V2 services
 * diff line-for-line.
 *
 * All responses are the standard ResponseModel `{ Message, Status, Data }`; the
 * helpers below unwrap `.Data` and throw on a non-success `Status`
 * (APIStatus.Successful === 0) so callers can `await` the inner payload
 * directly and `catch` a friendly Error.
 *
 * 🔴 404 IS A REAL OUTCOME HERE: `getTurn(turnId)` returns 404 for a turn that
 * does not exist OR belongs to another company (deliberately indistinguishable
 * — spec §API Contract line 188/432). `APIStatus` has no member for 400, so 400
 * and 500 both arrive as `Status = Error (1)`; the ONLY way a caller can tell
 * "not found" from "bad request" is `err.httpStatus` set by `toError()` below.
 * `utils/taxPlaygroundV2Store.js` relies on that for the L15 empty state.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs in
 * `utils/taxPlaygroundV2Store.js`. Payloads are camelCase end-to-end (the DTOs
 * are camelCase) with ONE deliberate exception: `configForm.counts` /
 * `turnSummary.counts` keys are **snake_case** (`not_started`,
 * `partial_answer`, `not_applicable`) because `ConfigFormV2CountsDto` pins them
 * with `[JsonPropertyName]` (`Model/AppModel/TaxPlaygroundV2Models.cs:385-393`).
 *
 * NOTE (id casing): every id ends in `…Id` — `turnId`, `formId`, `yearId`,
 * `questionItemId`. NOT `…ID`. The TaxConfig V2 DTOs really do use `yearID` /
 * `versionID`; do not copy that habit into this file.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 * Accepts either PascalCase (`Status`/`Data`) or camelCase (`status`/`data`).
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function patch(url, payload) {
  try {
    return unwrap(await http.patch(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
// `put`/`patch`/`del` are unused by the 6 playground endpoints on purpose — the
// skeleton is kept byte-identical to the sibling V2 services so the three files
// stay diffable; Vite tree-shakes them out of the bundle.

const ROOT = 'api/v1/TaxPlaygroundV2';

export const taxPlaygroundV2Api = {
  // ── Bootstrap ────────────────────────────────────────────────────────
  /**
   * Years/config versions/question versions INCLUDING drafts + the Tax Planning
   * Settings V2 versions + the active-chain defaults the node would resolve on
   * its own (`defaults.settingsVersionId` = the ACTIVE settings version).
   * `models[]` is still returned but DEPRECATED — the console picks a settings
   * version instead of overriding the model per turn.
   * @returns {Promise<Object>} PlaygroundOptions { years[], settingsVersions[], models[], defaults }
   */
  getOptions() {
    return get(`${ROOT}/options`);
  },

  // ── Chat (one turn → direct node invocation) ─────────────────────────
  /**
   * Send one turn. Body is built by the store (`buildChatBody`):
   * `{ sessionId, userId, message, yearId, configVersionId, questionVersionId,
   *    settingsVersionId: number|null, channel: 'playground', persistTurn: true }`.
   * `settingsVersionId` REPLACED the old per-turn `model` override (still accepted by the
   * backend, no longer read): null/0 ⇒ the ACTIVE `tax.TaxPlanningSettingsV2` version.
   * The version triple is all-or-nothing: send all three to override, otherwise
   * send null for all three and let the node resolve the active chain (spec line 251).
   * @param {Object} body TaxPlaygroundChatRequest
   * @returns {Promise<Object>} TurnDto (same shape as getTurn)
   */
  sendChat(body) {
    return post(`${ROOT}/chat`, body);
  },

  // ── Peek current state (no LLM call, no write) ───────────────────────
  /**
   * @param {string} sessionId required — free text, encoded
   * @param {?string} userId optional — only appended when a non-empty string
   * @param {?number} settingsVersionId optional — which `tax.TaxPlanningSettingsV2` version the
   *   greeting / resume / year-select copy is read from. 🔴 Pass the SAME id `sendChat` sends,
   *   otherwise the peek previews a different version's wording from the one the turn will run.
   *   Omitted from the query when null/0 ⇒ the backend uses the ACTIVE version.
   * @param {?{yearId: ?number, configVersionId: ?number, questionVersionId: ?number}} [triple] optional —
   *   the version triple the NEXT `/chat` turn will force (the store's `overrideTriple()`). 🔴 Pass the SAME
   *   triple `sendChat` sends: a finished plan whose config/question pair differs from it (same year) peeks as
   *   the version-choice screen, and the node asks that screen on exactly the same condition — a peek on another
   *   triple would draw a screen the next turn does not agree with. All-or-nothing like `/chat`: appended only
   *   when all three are > 0, otherwise none is sent ⇒ the backend uses the default `/chat` would synthesize.
   * @returns {Promise<Object>} StateDto { hasForm, formId, taxState, configForm,
   *   nextQuestion, quickReply[], quickReplySource, progress, versions, lastTurnId }
   */
  getState(sessionId, userId, settingsVersionId, triple) {
    let url = `${ROOT}/state?sessionId=${encodeURIComponent(sessionId ?? '')}`;
    if (typeof userId === 'string' && userId !== '') {
      url += `&userId=${encodeURIComponent(userId)}`;
    }
    const sv = Number(settingsVersionId);
    if (Number.isFinite(sv) && sv > 0) {
      url += `&settingsVersionId=${encodeURIComponent(sv)}`;
    }
    const t = triple || {};
    const y = Number(t.yearId);
    const cv = Number(t.configVersionId);
    const qv = Number(t.questionVersionId);
    if ([y, cv, qv].every((n) => Number.isFinite(n) && n > 0)) {
      url += `&yearId=${encodeURIComponent(y)}`;
      url += `&configVersionId=${encodeURIComponent(cv)}`;
      url += `&questionVersionId=${encodeURIComponent(qv)}`;
    }
    return get(url);
  },

  // ── Reset (abandon the session's current form) ───────────────────────
  /**
   * Body: `{ sessionId, userId, yearId, configVersionId, questionVersionId, clearTurns }`.
   * The version triple is echo-only (no pre-seed in this slice — the new form is
   * created by the NEXT /chat turn, so `newFormId` is always 0).
   * @param {Object} body TaxPlaygroundResetRequest
   * @returns {Promise<Object>} { abandonedFormId, newFormId, clearedTurnCount }
   */
  reset(body) {
    return post(`${ROOT}/reset`, body);
  },

  // ── Snapshot export / import (the fast-forward button) ───────────────
  /**
   * Snapshot the session's current form — state + every row the customer
   * produced — as a portable fixture.
   * 🔴 404 (no form for this session, or another company's) surfaces as a thrown
   * Error with `err.httpStatus === 404`; the caller shows an empty state, not a
   * red error.
   * @param {string} sessionId
   * @returns {Promise<Object>} TaxPlaygroundFormSnapshotDto
   */
  exportForm(sessionId, userId) {
    // userId is optional and additive — the backend falls back to the session lookup without it. It is sent
    // because the playground resolves a form by PG USER now, so the form on screen is often not one this
    // session created.
    let url = `${ROOT}/export?sessionId=${encodeURIComponent(sessionId ?? '')}`;
    if (typeof userId === 'string' && userId !== '') {
      url += `&userId=${encodeURIComponent(userId)}`;
    }
    return get(url);
  },
  /**
   * Fast-forward THIS session to a snapshot: the current form is abandoned and
   * the rows are replayed, so the next `/chat` turn continues from the point the
   * snapshot was taken.
   * 🔴 The server writes to the caller's own session / PG- user, never to the ids
   * inside the file — a pasted snapshot is untrusted input.
   * @param {Object} body `{ sessionId, userId, snapshot, clearTurns }`
   * @returns {Promise<Object>} { newFormId, abandonedFormId, clearedTurnCount,
   *   appliedCount, remappedCount, droppedCount, warnings[] }
   */
  importForm(body) {
    return post(`${ROOT}/import`, body);
  },

  // ── Turn timeline ────────────────────────────────────────────────────
  /**
   * Light timeline rows, newest → oldest (`ORDER BY TurnNo DESC` server-side).
   * The store re-sorts ASC by `turnId`. Paging is clamped server-side
   * (`page < 1 → 1`, `pageSize < 1 → 50`, cap 200) — do NOT clamp again here.
   * @returns {Promise<Object>} SearchResult<TurnSummary>
   *   { items[], totalCount, page, pageNumber, pageSize, totalPages } — NO `total`
   */
  listTurns(sessionId, page = 1, pageSize = 50) {
    const p = Number(page) || 1;
    const size = Number(pageSize) || 50;
    return get(`${ROOT}/turns?sessionId=${encodeURIComponent(sessionId ?? '')}&page=${p}&pageSize=${size}`);
  },
  /**
   * One turn, fully inflated — same payload shape as `sendChat`.
   * 🔴 404 (missing OR another company's turn) surfaces as a thrown Error with
   * `err.httpStatus === 404`; the store turns that into an empty state, not an error.
   * @param {number} turnId
   * @returns {Promise<Object>} TurnDto
   */
  getTurn(turnId) {
    return get(`${ROOT}/turns/${encodeURIComponent(turnId)}`);
  },
};

export default taxPlaygroundV2Api;
